import {inject, Injectable} from '@angular/core';
import {ReportReply, ReportStatus, SafetyReport} from '@models';
import {addDoc, collection, doc, getDocs, limit, orderBy, query, updateDoc, where} from 'firebase/firestore';
import {ProfileService} from '../community';
import {FirebaseInitService} from '../shared/firebase-init.service';

interface CreateReportCommand {
	category: SafetyReport['category'];
	description: string;
	source: string;
	reportedUserPseudo?: string;
}

@Injectable({
	providedIn: 'root'
})
export class ReportCommandService {
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly profileService = inject(ProfileService);

	private get collectionRef() {
		return collection(this.firebaseInit.firestore, 'reports');
	}

	async create(command: CreateReportCommand): Promise<string> {
		const currentProfile = this.profileService.profile();
		if (!currentProfile?.uuid) {
			throw new Error('Utilisateur non connecté');
		}
		const trimmedDescription = command.description.trim();
		if (trimmedDescription.length < 10 || trimmedDescription.length > 2000) {
			throw new Error('La description doit contenir entre 10 et 2000 caractères');
		}

		const lastUserReportQuery = query(
			this.collectionRef,
			where('reporterUid', '==', currentProfile.uuid),
			orderBy('createdAt', 'desc'),
			limit(1)
		);
		const lastUserReportSnapshot = await getDocs(lastUserReportQuery);
		const lastCreatedAt = lastUserReportSnapshot.docs[0]?.data()['createdAt'] as number | undefined;
		if (typeof lastCreatedAt === 'number' && (Date.now() - lastCreatedAt) < 60_000) {
			throw new Error('Merci de patienter une minute avant un nouveau signalement');
		}

		const now = Date.now();
		const payload: Omit<SafetyReport, 'uuid'> = {
			category: command.category,
			description: trimmedDescription,
			source: command.source,
			reporterUid: currentProfile.uuid,
			reporterUsername: currentProfile.username,
			status: 'open',
			createdAt: now,
			updatedAt: now,
			replies: []
		};
		if (command.reportedUserPseudo !== undefined) {
			payload.reportedUserPseudo = command.reportedUserPseudo;
		}
		const docRef = await addDoc(this.collectionRef, payload);
		return docRef.id;
	}

	async addReply(report: SafetyReport, message: string): Promise<void> {
		if (!report.uuid) {
			return;
		}
		const currentProfile = this.profileService.profile();
		if (!currentProfile?.uuid) {
			throw new Error('Utilisateur non connecté');
		}
		const reply: ReportReply = {
			message: message.trim(),
			createdAt: Date.now(),
			authorUid: currentProfile.uuid,
			authorRole: currentProfile.scope === 'Admin' ? 'admin' : 'user'
		};
		await updateDoc(doc(this.firebaseInit.firestore, 'reports', report.uuid), {
			replies: [...(report.replies ?? []), reply],
			updatedAt: Date.now()
		});
	}

	async updateStatus(reportId: string, status: ReportStatus): Promise<void> {
		await updateDoc(doc(this.firebaseInit.firestore, 'reports', reportId), {
			status,
			updatedAt: Date.now()
		});
	}
}
